import { useEffect, useRef, useState } from 'react';
import { useBasket } from '../context/BasketContext.jsx';
import { useStoreUI } from '../context/StoreUIContext.jsx';
import { useToast } from '../context/ToastContext.jsx';
import { useEscapeKey } from '../hooks/useEscapeKey.js';
import { avatarUrl } from '../lib/minecraft.js';
import '../styles/components/GiftModalStyle.css';

const USERNAME_PATTERN = /^\.?[A-Za-z0-9_]{2,17}$/;

export function GiftModal() {
  const { username, addGift } = useBasket();
  const { giftPackage, closeGift, openBasket } = useStoreUI();
  const { showToast } = useToast();
  const [recipient, setRecipient] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const inputRef = useRef(null);
  const open = Boolean(giftPackage);
  useEscapeKey(() => { if (!pending) closeGift(); }, open);
  useEffect(() => { if (open) window.setTimeout(() => inputRef.current?.focus(), 50); }, [open]);
  if (!open) return null;

  const submit = async () => {
    const target = recipient.trim();
    if (!USERNAME_PATTERN.test(target)) { setError('Enter a valid Minecraft username (2–17 chars).'); return; }
    if (target.toLowerCase() === String(username).toLowerCase()) { setError('Use Add to Basket when purchasing for yourself.'); return; }
    setPending(true); setError('');
    try {
      const result = await addGift(giftPackage.id, target);
      if (!result.success) { setError(result.error || 'Could not add this gift.'); return; }
      showToast(`${giftPackage.name} added as a gift for ${target}!`, 4500);
      setRecipient(''); closeGift(); openBasket();
    } catch { setError('The gift could not be added. Please try again.'); }
    finally { setPending(false); }
  };

  return <div className="gift-modal" role="dialog" aria-modal="true" aria-labelledby="gift-label gift-title" onClick={(e) => e.target === e.currentTarget && !pending && closeGift()}>
    <div className="gift-box">
      <button type="button" className="gift-close" aria-label="Close" disabled={pending} onClick={closeGift}>
        <i className="fa-solid fa-xmark" aria-hidden="true" />
      </button>
      <span id="gift-label" className="gift-eyebrow">Gift</span>
      <img src={avatarUrl(recipient.trim(), 160)} alt="" width="72" height="72" />
      <h2 id="gift-title">{giftPackage.name}</h2>
      <p>The full quantity will be delivered to this player.</p>
      <label htmlFor="gift-recipient">Recipient Minecraft username</label>
      <input id="gift-recipient" ref={inputRef} value={recipient} maxLength={18} disabled={pending} onChange={(e) => { setRecipient(e.target.value); setError(''); }} onKeyDown={(e) => e.key === 'Enter' && submit()} />
      <small>Bedrock players: prefix the username with a dot.</small>
      {error && <div className="gift-error" role="alert">
        <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
        <div>
          <strong>Couldn&apos;t add this gift</strong>
          <span>{error}</span>
        </div>
      </div>}
      <button type="button" className="gift-submit" disabled={pending} onClick={submit}>{pending ? 'Adding…' : 'Add Gift to Basket'}</button>
    </div>
  </div>;
}
